import React, { useContext, useEffect, useMemo, useRef } from 'react';
import CareerPathwayCard from './CareerPathwayCard';
import { RecommendedSectionProps } from '../types';
import tagularEvent from '@/utils/tagular/tagularEvent';
import uuid from '@/utils/uuid';
import ViewMoreExpandable from '@/components/DesignSystem/EditorialListings/ViewMoreExpandable';
import { MobiusContext } from '@/components/MobiusProvider';
import { useDynamicExperienceContext } from '@/components/DynamicFlow/providers/DynamicExperienceProvider';
import useFlowTrackingEvents from '@/components/DynamicFlow/hooks/useFlowTrackingEvent';
import { createResultsEventContext } from '@/components/DynamicFlow/utils/createResultsEventContext';
import { EventingLocation } from '@/components/DynamicFlow/utils/types/types';
import { EventContext } from '@/components/DynamicFlow/utils/createEventingPayloadsDE';

const RecommendedSection = ({
  recommendedItems,
  title,
  subtitle,
  orientation = 'vertical',
  correlationId,
  studyMatchDestination,
  selectedCurrentEducation,
  selectedFieldOfStudy,
}: RecommendedSectionProps) => {
  const { presets } = useContext(MobiusContext);
  const { eventContext } = useDynamicExperienceContext();
  const { triggerProductClickedEvent } = useFlowTrackingEvents();

  // Create a stable mapping of viewCorrelationId for each item
  const viewCorrelationIds = useMemo(() => {
    return recommendedItems.reduce(
      (acc, item) => {
        acc[item.id?.toString() || ''] = uuid();
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [recommendedItems]);

  const viewCorrelationId = uuid();

  useEffect(() => {
    recommendedItems.forEach((item, index) => {
      const itemViewCorrelationId =
        viewCorrelationIds[item.id?.toString() || ''];

      if (!eventContext.dynamicExperienceEventFlag) {
        tagularEvent('ProductViewed', {
          viewCorrelationId: itemViewCorrelationId,
          product: {
            brand: item.subheader || 'general', // School name
            category: presets?.category || 'general', // Form category from contextual defaults
            formatSubtype: 'career-pathways-card',
            formatType: 'dexp-tool-results',
            location: 'career-pathways',
            name: selectedFieldOfStudy?.slug || 'general', // Form subject
            variant: selectedCurrentEducation?.slug || 'general', // Form degree
            productId: item.id?.toString() || '',
            positionEngine: (index + 1).toString(),
          },
        });
      }
    });
  }, [recommendedItems]);

  const handleProgramClick = (
    item: any,
    index: number,
    destinationUrl: string,
  ) => {
    // Get the specific viewCorrelationId for this item
    const itemViewCorrelationId = viewCorrelationIds[item.id?.toString() || ''];

    if (!eventContext.dynamicExperienceEventFlag) {
      tagularEvent('ProductClicked', {
        viewCorrelationId: itemViewCorrelationId,
        correlationId:
          destinationUrl.split('correlationId=')[1]?.split('&')[0] ||
          correlationId,
        outboundUrl: destinationUrl,
        product: {
          brand: item.subheader || 'general',
          category: presets?.category || 'general',
          formatSubtype: 'career-pathway-card',
          formatType: 'dexp-tool-results',
          location: 'career-pathways',
          name: selectedFieldOfStudy?.slug || 'general',
          position: index + 1,
          productId: item.id?.toString() || '',
          variant: selectedCurrentEducation?.slug || 'general',
          positionEngine: (index + 1).toString(),
        },
      });
    } else {
      const resultEventContext = createResultsEventContext({
        eventContext,
        listId: item.listId,
        presets,
        location: EventingLocation.CAREER_PATHWAYS,
        productClickedCorrelationId: itemViewCorrelationId,
        viewCorrelationId: itemViewCorrelationId,
        brand: item.subheader || 'general',
        position: index + 1,
        capId: item.capId != null ? Number(item.capId) : undefined,
        programId: item.id,
        outboundUrl: destinationUrl,
        isProgramSubmitted: item.isProgramSubmitted ?? false,
        isUserRequested: item.isUserRequested ?? false,
      });
      triggerProductClickedEvent(resultEventContext);
    }

    // Navigate to the program
    window.open(destinationUrl, '_blank');
  };

  // Fire Career Pathway specific ElementViewed event for the View More button
  const viewMoreButtonRef = useRef<HTMLDivElement>(null);
  const hasViewedRef = useRef(false);

  useEffect(() => {
    if (!hasViewedRef.current && viewMoreButtonRef.current) {
      // Fire ElementViewed event with Career Pathway context
      tagularEvent('ElementViewed', {
        viewCorrelationId: viewCorrelationId,
        correlationId,
        webElement: {
          elementType: 'dexp-tool-link',
          text: 'View More Programs',
          htmlId: 'view-more-button',
          name: 'view-more-button',
          location: 'career-pathways',
          position: 'career-pathways',
        },
      });
      hasViewedRef.current = true;
    }
  }, []);

  return (
    <div className='bg-white rounded-xl flex flex-col w-full sticky top-1'>
      <span className='text-base md:text-lg font-bold text-gray-900 pb-2'>
        {title}
      </span>
      {subtitle && (
        <span className='text-gray-600 pb-2 md:pb-6 text-xs md:text-sm font-normal'>
          For {subtitle}
        </span>
      )}
      <div
        className={` ${
          orientation === 'horizontal'
            ? 'flex flex-col md:flex-row gap-2.5'
            : 'grid grid-cols-1 gap-4 overflow-y-auto last:mb-4'
        }`}
      >
        {recommendedItems.map((item, index) => (
          <div key={item.id} className='last:mb-4'>
            <CareerPathwayCard
              key={item.id}
              subheader={item.subheader}
              title={item.title}
              description={item.description}
              destinationUrl={item.destinationUrl}
              correlationId={correlationId}
              logo={item.logo}
              onProgramClick={() =>
                handleProgramClick(item, index, item.destinationUrl || '')
              }
              eventContext={eventContext}
              itemViewCorrelationId={
                viewCorrelationIds[item.id?.toString() || '']
              }
              presets={presets}
              programId={item.id}
            />
          </div>
        ))}
      </div>
      <div className='pt-2 md:pt-6' ref={viewMoreButtonRef}>
        <ViewMoreExpandable
          url={studyMatchDestination}
          onClick={() => {}}
          label='View More Programs'
          shouldLinkOut={true}
          location='career-pathways'
          position='career-pathways'
          inheritedCorrelationId={viewCorrelationId}
          isDexpTool={true}
        />
      </div>
    </div>
  );
};

export default RecommendedSection;
